import {
  Box,
  Button,
  Flex,
  Icon,
  Skeleton,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaDownload } from "react-icons/fa";
import { ContractListItemDto } from "../../../autogen/bff-api";
import { HorizontalScrollBox } from "../../../common/HorizontalScrollBox";
import { Paging } from "../../../common/Paging";
import { useSub } from "../../../common/subscription/useSub";
import { Explanation } from "../../../common/support/Explanation/Explanation";
import { downloadSelectedContractsIfrsReport } from "../ifrsReportDownload";
import { ContractRow } from "./ContractRow";

interface Props {
  contracts: ContractListItemDto[];
  isLoading: boolean;
  skip: number;
  limit: number;
  setSkip?: (skip: number) => void;
}

export const ContractsTable = ({ contracts, isLoading, skip, limit, setSkip }: Props) => {
  const { t } = useTranslation();
  const sub = useSub();

  const [ifrsReportingEnabled, setIfrsReportingEnabled] = useState<boolean>(false);
  const [idsSelectedForIfrsReporting, setIdsSelectedForIfrsReporting] = useState<string[]>([]);
  const [isDownloadingIfrsReport, setIsDownloadingIfrsReport] = useState(false);
  const toast = useToast();
  const columnCount = ifrsReportingEnabled ? 9 : 8;
  const hasNextPage = contracts.length >= limit;

  const downloadIfrsReport = async () => {
    setIsDownloadingIfrsReport(true);
    try {
      await downloadSelectedContractsIfrsReport(idsSelectedForIfrsReporting);
    } catch {
      toast({ description: t("The report could not be downloaded. Try again."), status: "error" });
    } finally {
      setIsDownloadingIfrsReport(false);
    }
  };

  return (
    <Flex flexDirection="column" width="full" maxWidth="full" overflow="hidden" position="relative">
      <Flex
        flexDirection="column"
        bg="smBackground"
        border="1px solid"
        borderColor="smBorder"
        borderRadius="lg"
        width="full"
        maxWidth="full"
      >
        {setSkip && (
          <Paging skip={skip} limit={limit} setSkip={setSkip} isLoading={isLoading} hasNextPage={hasNextPage} />
        )}
        <HorizontalScrollBox>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th minWidth="80">{t("Title")}</Th>
                <Th>{t("Counterparty")}</Th>
                <Th>{t("Internal party")}</Th>
                <Th>{t("Expiration")}</Th>
                <Th>{t("Responsible")}</Th>
                <Th>{t("Status")}</Th>
                <Th>{t("Type")}</Th>
                <Th>{t("Owner")}</Th>
                {ifrsReportingEnabled && <Th></Th>}
              </Tr>
            </Thead>
            <Tbody>
              <>
                {isLoading &&
                  contracts.length === 0 &&
                  Array.from({ length: Math.min(limit, 5) }, (_, rowIndex) => (
                    <Tr key={rowIndex}>
                      {Array.from({ length: columnCount }, (_, cellIndex) => (
                        <Td key={cellIndex}>
                          <Skeleton h="5" w="full" rounded="md" />
                        </Td>
                      ))}
                    </Tr>
                  ))}
                {contracts.map((e) => {
                  return (
                    <ContractRow
                      key={e.id}
                      contract={e}
                      ifrsReportingEnabled={ifrsReportingEnabled}
                      selectedForIfrsReporting={idsSelectedForIfrsReporting.includes(e.id)}
                      selectedForIfrsReportingChanged={(isSelected) => {
                        if (isSelected) {
                          setIdsSelectedForIfrsReporting([...idsSelectedForIfrsReporting, e.id]);
                        } else {
                          setIdsSelectedForIfrsReporting(idsSelectedForIfrsReporting.filter((id) => id !== e.id));
                        }
                      }}
                    />
                  );
                })}
              </>
            </Tbody>
          </Table>
        </HorizontalScrollBox>
        {!isLoading && contracts.length === 0 && (
          <Flex justify="center" py="10" px="4" textAlign="center">
            <Text color="smMuted">{t("No contracts found")}</Text>
          </Flex>
        )}
        {setSkip && (
          <Paging skip={skip} limit={limit} setSkip={setSkip} isLoading={isLoading} hasNextPage={hasNextPage} />
        )}
      </Flex>
      {sub.hasIFRSAccess && (
        <>
          {!ifrsReportingEnabled ? (
            <Flex>
              <Spacer />
              <Button variant={"solid"} size="sm" mt="15px" onClick={() => setIfrsReportingEnabled(true)}>
                {t("IFRS reporting")}
              </Button>
            </Flex>
          ) : (
            <Flex>
              <Spacer />
              <Button variant={"solid"} size="sm" mt="15px" onClick={() => setIfrsReportingEnabled(false)}>
                {t("Cancel")}
              </Button>
              <Box mt="15px" ml="5px">
                <Explanation
                  text={t("Please select at least one contract to download the IFRS report for")}
                  enabled={idsSelectedForIfrsReporting.length === 0}
                >
                  <Button
                    size="sm"
                    leftIcon={<Icon as={FaDownload} w="15px" h="15px" />}
                    isDisabled={idsSelectedForIfrsReporting.length === 0}
                    isLoading={isDownloadingIfrsReport}
                    variant={"solid"}
                    colorScheme="blue"
                    onClick={downloadIfrsReport}
                  >
                    {t("Download IFRS report")}
                  </Button>
                </Explanation>
              </Box>
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
};
